import {defaultComposer} from 'default-composer'

import {usePage} from '../context/PageContext'
import {usePanels} from '../context/PanelContext'

import type {XcNwBlockFragment} from '@sdox/api'

type Siblings = {
  prev: XcNwBlockFragment | null
  next: XcNwBlockFragment | null
}

const defaultNullObj: Siblings = {prev: null, next: null}

/**
 * Returns an object containing the logical previous and next sibling of a given block inside page.
 * Throws an error when not used inside a PageContext or PanelContext
 * @param block
 */
export const useBlockSiblings = (block: XcNwBlockFragment): Siblings => {
  const page = usePage()
  const panels = usePanels() // This is only used in case the block is inside an accordion
  if (!page) throw new Error('Programming error - useBlockSiblings is only allowed in PageContext')

  let pageBlocks = page.panels.flatMap(block => block.blocks)

  //  we need to verify the typename as well since some blocks can collide with the same ID.
  let blockIndex = pageBlocks.findIndex(({__typename, id}) => (
    block.id === id && block.__typename === __typename
  ))

  // block is likely inside an accordion, so we check for the panel instead of the page
  if (blockIndex === -1) {
    const accordionPanel = panels[0]
    if (accordionPanel === undefined) return defaultNullObj

    pageBlocks = accordionPanel.blocks
    //  we need to verify the typename as well since some blocks can collide with the same ID.
    blockIndex = pageBlocks.findIndex(({__typename, id}) => (
      block.id === id && block.__typename === __typename
    ))
  }

  return defaultComposer(defaultNullObj, {
    prev: pageBlocks[blockIndex -1],
    next: pageBlocks[blockIndex +1],
  })
}
